import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import MindfullLogo from '@/components/MindfullLogo';
import { MindfullMark } from '@/components/brand/MindfullMark';

const Signup = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  
  // Waitlist state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Redirect logged-in users to canvas
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/canvas');
    }
  }, [user, isLoading, navigate]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!waitlistEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert({ email: waitlistEmail.toLowerCase().trim() });
      
      if (error) {
        if (error.code === '23505') {
          toast.info("You're already on the list!");
          setIsSubmitted(true);
        } else {
          throw error;
        }
      } else {
        setIsSubmitted(true);
        toast.success("You're on the list!");
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <Loader2 className="h-8 w-8 animate-spin text-ink" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper p-4 sm:p-6 lg:p-8">
      <div className="bg-paper-raised rounded-[2rem] sm:rounded-[3rem] min-h-[calc(100vh-2rem)] sm:min-h-[calc(100vh-3rem)] lg:min-h-[calc(100vh-4rem)] shadow-xl overflow-hidden flex flex-col">
        {/* Simple Header */}
        <header className="p-6 sm:px-10 lg:px-16">
          <Link to="/" className="inline-flex">
            <MindfullLogo size="md" />
          </Link>
        </header>

        {/* Signup Card */}
        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <Card className="w-full max-w-md shadow-2xl border-border/50 rounded-3xl">
            <CardHeader className="text-center space-y-4 pb-2">
              <div className="mx-auto w-16 h-16 bg-paper-raised rounded-2xl flex items-center justify-center shadow-lg border border-border/30 text-ink">
                <MindfullMark size={34} motion="intro" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Join the Waitlist</CardTitle>
                <CardDescription className="text-base mt-2">
                  Mindfull is currently in private beta. Sign up to get early access.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6 pb-8 px-8">
              {isSubmitted ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-ink rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-paper-raised" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">You're on the list!</h3>
                  <p className="text-muted-foreground text-sm">
                    We'll notify you when Mindfull is ready. Keep an eye on your inbox.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      disabled={isSubmitting}
                      className="rounded-xl"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Joining...
                      </>
                    ) : (
                      <>
                        Join Waitlist
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              <p className="text-sm text-muted-foreground text-center mt-6">
                Already have access?{' '}
                <Link to="/login?access=true" className="text-ink hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Signup;
